import { useContext } from "react";

import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Button } from "@upstageai/ui";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { DownloadFileType, ViewType } from "../../constants/EndpointViews";
import { ValidatorFileContext } from "../../hooks/useFile";
import { InferenceResult, OacJsonResponse } from "../../types/demo";
import { ExtractorOacJsonResponse } from "../../types/extractorOacResponse";
import { Group, Line } from "../../types/extractorResult";
import {
  getFilteredLAJson,
  LAElement,
  LAOacJsonResponse,
} from "../../types/laOacResponse";
import { Line as OCRLine } from "../../types/ocrOacResponse";
import { loadExternalScript } from "../../utils/downloadFile";
import { endpointStore } from "../../utils/mobx/EndpointStore";

interface ResultDownloadButtonProps {
  viewType: ViewType;
  buttonVariant?: "ghost" | "outline";
}

export interface DownloadParams {
  json: OacJsonResponse | null;
  requestId: string | null;
  token?: string | null;
  fileNameWithoutExtenstion: string;
  trans: any;
  endpointType: string;
  downloadFileType: DownloadFileType;
}

type changedResult = Group | Line;

const _makeExtractorJson = (
  groups: Group[],
  lines: Line[],
  result: ExtractorOacJsonResponse,
): InferenceResult => {
  const orgArr = result.fields;
  //orgArr에 있는 id를 key로 하는 map을 만들어준다.
  const orgMap = new Map<number, Group | Line>();
  orgArr.forEach(v => {
    orgMap.set(v.id, v);
  });

  const arr: changedResult[] = [];
  let groupIdx = 0;
  let lineIdx = 0;
  while (groupIdx < groups.length && lineIdx < lines.length) {
    // reulst에 있는 id의 경우 vertices를 result에 있게 변경하여 넣어주고, 없는 경우는 그냥 넣어준다.

    if (groups[groupIdx].id > lines[lineIdx].id) {
      const item = _.cloneDeep(lines[lineIdx]);
      if (orgMap.has(item.id)) {
        item.boundingBoxes = (orgMap.get(item.id) as Line).boundingBoxes;
      }
      arr.push(item);
      lineIdx += 1;
    } else {
      const item = _.cloneDeep(groups[groupIdx]);
      if (orgMap.has(item.id)) {
        item.properties.forEach(p => {
          p.boundingBoxes = (orgMap.get(item.id) as Group).properties.find(
            v => v.id === p.id,
          )?.boundingBoxes;
        });
      }
      arr.push(item);
      groupIdx += 1;
    }
  }
  while (lineIdx < lines.length) {
    arr.push(lines[lineIdx]);
    lineIdx += 1;
  }

  while (groupIdx < groups.length) {
    arr.push(groups[groupIdx]);
    groupIdx += 1;
  }
  return JSON.parse(JSON.stringify(arr)) as InferenceResult;
};

const _makeOCRJson = (ocrLines: OCRLine[]) => {
  return JSON.parse(JSON.stringify(ocrLines)) as InferenceResult;
};

const _makeLAJson = (laElements: LAElement[]) => {
  return JSON.parse(JSON.stringify(laElements)) as InferenceResult;
};

export async function download({
  json,
  requestId,
  token,
  fileNameWithoutExtenstion,
  trans,
  endpointType,
  downloadFileType,
}: DownloadParams): Promise<void> {
  try {
    // REACT_APP_SERVER_URL이 없으면 window.location.origin + "/custom"을 사용
    const baseUrl = process.env.REACT_APP_SERVER_URL
      ? process.env.REACT_APP_SERVER_URL.startsWith("http")
        ? process.env.REACT_APP_SERVER_URL
        : window.location.origin + process.env.REACT_APP_SERVER_URL
      : window.location.origin + "/custom";

    await loadExternalScript(`${baseUrl}/ButtonEvent.js`);

    if (!window.DownloadEvent) {
      throw new Error("DownloadEvent function not found");
    }

    window.DownloadEvent({
      json,
      requestId,
      token,
      fileNameWithoutExtenstion,
      trans,
      endpointType,
      downloadFileType,
    });
  } catch (error) {
    console.error("Error downloading result:", error);
  }
}

export default function ResultDownloadButton({
  viewType,
  buttonVariant = "ghost",
}: ResultDownloadButtonProps) {
  const { t } = useTranslation();
  const { endpointType } = endpointStore;
  const { inferenceState, files, selectedFileIndex } =
    useContext(ValidatorFileContext);

  const [searchParams, _] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const token = searchParams.get("token");

  if (inferenceState.isInfering || viewType === "preview") return null;

  const handleDownload = (downloadFileType: DownloadFileType) => {
    // let outputJson: InferenceResult = [];
    // if (endpointType.toLowerCase() === "extractor") {
    //   outputJson = makeExtractorJson(sortedGroups, lineGroups);
    // } else if (endpointType.toLowerCase() === "ocr") {
    //   outputJson = makeOCRJson(ocrLines);
    // } else if (endpointType.toLowerCase() === "layout-analysis") {
    //   outputJson = makeLAJson(laElements);
    // }

    const originalJson = files[selectedFileIndex].result as OacJsonResponse;

    // Spec과 동일시하기 위해 imageBase64 제거하여 보여줌
    const { imageBase64: _imageBase64, ...jsonWithoutImage } = originalJson;

    const filteredJson =
      endpointType === "layout-analysis"
        ? getFilteredLAJson(jsonWithoutImage as LAOacJsonResponse)
        : jsonWithoutImage;

    download({
      json: filteredJson as OacJsonResponse,
      requestId: requestId,
      token: token,
      fileNameWithoutExtenstion: "try_upstage_document_ai",
      trans: t,
      endpointType: endpointType,
      downloadFileType,
    });
  };

  return (
    <Button
      variant={buttonVariant}
      size="icon"
      className="aspect-square h-9 w-9 p-1"
      onClick={() => handleDownload(viewType)}
    >
      <ArrowDownTrayIcon className="h-5 w-5" />
    </Button>
  );
}
